import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  RotateCcw,
  FileText,
  Pencil,
  Save,
  X,
  Plus,
  Trash2,
  CalendarIcon,
  AlertTriangle,
  ArrowRightLeft,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import Attachments from './Attachments';
import { HelpPanel } from '../components/ui/help-panel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEBIT_CREDIT_TABLE = [
  { type: 'Assets', example: 'Cash, Equipment, A/R', debit: 'Increase', credit: 'Decrease', debitColor: 'text-emerald-600 dark:text-emerald-400', creditColor: 'text-red-500 dark:text-red-400' },
  { type: 'Liabilities', example: 'A/P, Loans, Credit Cards', debit: 'Decrease', credit: 'Increase', debitColor: 'text-red-500 dark:text-red-400', creditColor: 'text-emerald-600 dark:text-emerald-400' },
  { type: 'Equity', example: 'Owner\'s Equity, Retained Earnings', debit: 'Decrease', credit: 'Increase', debitColor: 'text-red-500 dark:text-red-400', creditColor: 'text-emerald-600 dark:text-emerald-400' },
  { type: 'Revenue', example: 'Sales, Service Income', debit: 'Decrease', credit: 'Increase', debitColor: 'text-red-500 dark:text-red-400', creditColor: 'text-emerald-600 dark:text-emerald-400' },
  { type: 'Expenses', example: 'Rent, Utilities, Payroll', debit: 'Increase', credit: 'Decrease', debitColor: 'text-emerald-600 dark:text-emerald-400', creditColor: 'text-red-500 dark:text-red-400' },
];

const debitCreditHelp = {
  title: "Debit & Credit Guide",
  description: "Quick reference for how debits and credits affect each account type",
  sections: [
    {
      heading: "The Golden Rule",
      content: (
        <div className="space-y-2">
          <p>Every journal entry must have <strong>equal debits and credits</strong>. This is the foundation of double-entry accounting.</p>
          <div className="rounded-lg border overflow-hidden mt-3">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left p-2 font-semibold">Account Type</th>
                  <th className="text-center p-2 font-semibold">Debit</th>
                  <th className="text-center p-2 font-semibold">Credit</th>
                </tr>
              </thead>
              <tbody>
                {DEBIT_CREDIT_TABLE.map((row) => (
                  <tr key={row.type} className="border-t">
                    <td className="p-2">
                      <div className="font-medium">{row.type}</div>
                      <div className="text-muted-foreground text-[10px]">{row.example}</div>
                    </td>
                    <td className={`p-2 text-center font-semibold ${row.debitColor}`}>
                      {row.debit === 'Increase' ? '+ Increase' : '- Decrease'}
                    </td>
                    <td className={`p-2 text-center font-semibold ${row.creditColor}`}>
                      {row.credit === 'Increase' ? '+ Increase' : '- Decrease'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      heading: "Common Examples",
      type: "info",
      content: (
        <div className="space-y-3 text-xs">
          <div className="p-2 rounded border bg-muted/30">
            <p className="font-medium mb-1">Paying rent ($1,000):</p>
            <div className="grid grid-cols-2 gap-1">
              <span className="text-emerald-600 dark:text-emerald-400">Debit: Rent Expense +$1,000</span>
              <span className="text-red-500 dark:text-red-400">Credit: Cash -$1,000</span>
            </div>
          </div>
          <div className="p-2 rounded border bg-muted/30">
            <p className="font-medium mb-1">Receiving payment from a customer ($500):</p>
            <div className="grid grid-cols-2 gap-1">
              <span className="text-emerald-600 dark:text-emerald-400">Debit: Cash +$500</span>
              <span className="text-red-500 dark:text-red-400">Credit: Accounts Receivable -$500</span>
            </div>
          </div>
          <div className="p-2 rounded border bg-muted/30">
            <p className="font-medium mb-1">Taking out a loan ($10,000):</p>
            <div className="grid grid-cols-2 gap-1">
              <span className="text-emerald-600 dark:text-emerald-400">Debit: Cash +$10,000</span>
              <span className="text-red-500 dark:text-red-400">Credit: Loan Payable +$10,000</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      heading: "Easy Memory Trick",
      content: (
        <div className="space-y-2">
          <p>Think of the accounting equation:</p>
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-center font-mono text-sm font-semibold">
            Assets = Liabilities + Equity
          </div>
          <p className="text-xs">
            <strong>Left side</strong> (Assets): Debits increase, Credits decrease.<br/>
            <strong>Right side</strong> (Liabilities, Equity, Revenue): Credits increase, Debits decrease.<br/>
            <strong>Expenses</strong> are the opposite of Revenue: Debits increase, Credits decrease.
          </p>
        </div>
      ),
    },
  ],
  tips: [
    "Debits always go on the left, Credits always go on the right",
    "Total debits MUST equal total credits for every entry",
    "Revenue and Expenses are temporary equity accounts - they reset each year",
    "When in doubt, think about what's happening to Cash",
  ],
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
};

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'status-draft',
    posted: 'status-posted',
    voided: 'status-voided',
    reversed: 'status-reversed',
  };
  
  return (
    <Badge variant="secondary" className={cn('text-sm capitalize', styles[status])}>
      {status}
    </Badge>
  );
};

export default function JournalEntryDetail() {
  const { entryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  
  // Entry state
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Action states
  const [posting, setPosting] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedMemo, setEditedMemo] = useState('');
  const [editedDate, setEditedDate] = useState(null);
  const [editedLines, setEditedLines] = useState([]);
  
  // Accounts for dropdown
  const [accounts, setAccounts] = useState([]);
  
  // Reverse/Replace dialog state
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [editOptionsOpen, setEditOptionsOpen] = useState(false);

  const fetchEntry = useCallback(async () => {
    if (!currentWorkspace || !entryId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entryId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setEntry(data);
        
        // Check if we should enter edit mode from URL param
        if (searchParams.get('edit') === 'true' && data.status === 'draft') {
          enterEditMode(data);
          // Clean up URL
          searchParams.delete('edit');
          setSearchParams(searchParams, { replace: true });
        }
      } else {
        toast.error('Entry not found');
        navigate('/journal-entries');
      }
    } catch (error) {
      console.error('Failed to fetch entry:', error);
      toast.error('Failed to load entry');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, entryId, searchParams, navigate, setSearchParams]);

  const fetchAccounts = useCallback(async () => {
    if (!currentWorkspace) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/accounts`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchEntry();
    fetchAccounts();
  }, [fetchEntry, fetchAccounts]);

  const enterEditMode = (entryData = entry) => {
    if (!entryData) return;
    setEditedMemo(entryData.memo || '');
    setEditedDate(entryData.txn_date ? new Date(entryData.txn_date + 'T00:00:00') : new Date());
    setEditedLines(entryData.lines.map(line => ({
      ...line,
      account_id: line.account_id,
      description: line.description || '',
      debit: line.debit || 0,
      credit: line.credit || 0
    })));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditedMemo('');
    setEditedDate(null);
    setEditedLines([]);
  };

  const handleEditClick = () => {
    if (entry?.status === 'draft') {
      enterEditMode();
    } else if (entry?.status === 'posted') {
      setEditOptionsOpen(true);
    }
  };

  const addLine = () => {
    setEditedLines([...editedLines, {
      account_id: '',
      description: '',
      debit: 0,
      credit: 0
    }]);
  };

  const removeLine = (index) => {
    setEditedLines(editedLines.filter((_, i) => i !== index));
  };

  const updateLine = (index, field, value) => {
    const updated = [...editedLines];
    
    if (field === 'debit' || field === 'credit') {
      const numValue = parseFloat(value) || 0;
      updated[index][field] = numValue;
      // Clear the opposite field when entering a value
      if (numValue > 0) {
        updated[index][field === 'debit' ? 'credit' : 'debit'] = 0;
      }
    } else {
      updated[index][field] = value;
    }
    
    setEditedLines(updated);
  };

  const calculateTotals = () => {
    const totalDebit = editedLines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredit = editedLines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    return { totalDebit, totalCredit, isBalanced: Math.abs(totalDebit - totalCredit) < 0.01 };
  };

  const handleSave = async () => {
    if (!entry || entry.status !== 'draft') return;
    
    const { totalDebit, totalCredit, isBalanced } = calculateTotals();
    
    // Validate lines
    const validLines = editedLines.filter(line => 
      line.account_id && (line.debit > 0 || line.credit > 0)
    );
    
    if (validLines.length < 2) {
      toast.error('Entry must have at least 2 valid lines');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entryId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            txn_date: format(editedDate, 'yyyy-MM-dd'),
            memo: editedMemo,
            lines: validLines.map(line => ({
              account_id: line.account_id,
              description: line.description,
              debit: parseFloat(line.debit) || 0,
              credit: parseFloat(line.credit) || 0,
              currency: 'USD',
              fx_rate: 1,
              dims: {}
            }))
          })
        }
      );
      
      if (response.ok) {
        toast.success('Entry saved successfully');
        setIsEditing(false);
        fetchEntry();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save entry');
      }
    } catch (error) {
      console.error('Failed to save entry:', error);
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handlePost = async () => {
    if (!entry || entry.status !== 'draft') return;
    
    if (entry.total_debit !== entry.total_credit) {
      toast.error('Entry is not balanced. Debits must equal Credits.');
      return;
    }

    setPosting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entryId}/post`,
        {
          method: 'POST',
          }
      );

      if (response.ok) {
        toast.success('Entry posted successfully');
        fetchEntry();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post entry');
      }
    } catch (error) {
      console.error('Failed to post entry:', error);
      toast.error('Failed to post entry');
    } finally {
      setPosting(false);
    }
  };

  const handleVoid = async () => {
    if (!entry || entry.status !== 'posted') return;

    setVoiding(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entryId}/void`,
        {
          method: 'POST',
          }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Entry voided. Reversal entry #${data.reversal_entry_no} created.`);
        setVoidDialogOpen(false);
        fetchEntry();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to void entry');
      }
    } catch (error) {
      console.error('Failed to void entry:', error);
      toast.error('Failed to void entry');
    } finally {
      setVoiding(false);
    }
  };

  const handleReverse = async () => {
    if (!entry || entry.status !== 'posted') return;
    
    setPosting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entryId}/reverse`,
        {
          method: 'POST',
          }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Entry reversed. Reversal entry #${data.entry_no} created.`);
        setReverseDialogOpen(false);
        fetchEntry();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reverse entry');
      }
    } catch (error) {
      console.error('Failed to reverse entry:', error);
      toast.error('Failed to reverse entry');
    } finally {
      setPosting(false);
    }
  };

  const handleReplace = async () => {
    if (!entry || entry.status !== 'posted') return;
    
    setPosting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/journal-entries/${entryId}/replace`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Entry reversed. Draft replacement #${data.replacement_entry_no} created.`);
        setReplaceDialogOpen(false);
        setEditOptionsOpen(false);
        navigate(`/journal-entries/${data.replacement_entry_id}?edit=true`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to replace entry');
      }
    } catch (error) {
      console.error('Failed to replace entry:', error);
      toast.error('Failed to replace entry');
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Entry not found</p>
      </div>
    );
  }

  const isBalanced = entry.total_debit === entry.total_credit;
  const canEdit = entry.status === 'draft';
  const canReverse = entry.status === 'posted';
  const isImmutable = entry.status === 'voided' || entry.status === 'reversed';
  const editTotals = isEditing ? calculateTotals() : null;

  return (
    <div className="space-y-6" data-testid="journal-entry-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/journal-entries">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                Journal Entry #{entry.entry_no}
              </h1>
              <StatusBadge status={entry.status} />
            </div>
            <p className="text-muted-foreground">
              Transaction Date: <span className="font-mono">{entry.txn_date}</span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={cancelEdit} disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !editTotals?.isBalanced}>
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {canEdit && (
                <>
                  <Button variant="outline" onClick={handleEditClick} data-testid="edit-entry-btn">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={handlePost}
                    disabled={posting || !isBalanced}
                    data-testid="post-entry-btn"
                  >
                    {posting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Post Entry
                      </>
                    )}
                  </Button>
                </>
              )}
              
              {canReverse && (
                <>
                  <Button variant="outline" onClick={handleEditClick} data-testid="edit-posted-btn">
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => setVoidDialogOpen(true)}
                    data-testid="void-entry-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Void Entry
                  </Button>
                </>
              )}
            </>
          )}
          <HelpPanel {...debitCreditHelp} />
        </div>
      </div>

      {/* Immutable Entry Warning */}
      {isImmutable && (
        <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              <p className="font-medium">This entry is {entry.status} and cannot be edited</p>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {entry.status === 'reversed' && entry.source_type === 'reversal' 
                ? 'This is a reversal entry.'
                : `This entry was ${entry.status} and its effects have been reversed.`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Edit Mode Form */}
      {isEditing && (
        <>
          {/* Edit Balance Warning */}
          {editTotals && !editTotals.isBalanced && (
            <Card className="border-destructive bg-destructive/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-destructive">
                  <XCircle className="w-5 h-5" />
                  <p className="font-medium">Entry Not Balanced</p>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Total Debit ({formatCurrency(editTotals.totalDebit)}) does not equal Total Credit ({formatCurrency(editTotals.totalCredit)}). 
                  Difference: {formatCurrency(Math.abs(editTotals.totalDebit - editTotals.totalCredit))}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Edit Form */}
          <Card>
            <CardHeader>
              <CardTitle>Edit Entry</CardTitle>
              <CardDescription>Modify the journal entry details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Transaction Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {editedDate ? format(editedDate, 'MMM d, yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editedDate}
                        onSelect={setEditedDate}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Memo</Label>
                  <Textarea 
                    value={editedMemo}
                    onChange={(e) => setEditedMemo(e.target.value)}
                    placeholder="Enter memo..."
                    className="resize-none"
                  />
                </div>
              </div>

              {/* Lines Editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Journal Lines</Label>
                  <Button variant="outline" size="sm" onClick={addLine}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Line
                  </Button>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Account</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-32 text-right">Debit</TableHead>
                      <TableHead className="w-32 text-right">Credit</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editedLines.map((line, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Select 
                            value={line.account_id} 
                            onValueChange={(value) => updateLine(index, 'account_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {accounts.map((acct) => (
                                <SelectItem key={acct.account_id} value={acct.account_id}>
                                  {acct.code} - {acct.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={line.description}
                            onChange={(e) => updateLine(index, 'description', e.target.value)}
                            placeholder="Description"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.debit || ''}
                            onChange={(e) => updateLine(index, 'debit', e.target.value)}
                            className="text-right font-mono"
                            placeholder="0.00"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={line.credit || ''}
                            onChange={(e) => updateLine(index, 'credit', e.target.value)}
                            className="text-right font-mono"
                            placeholder="0.00"
                          />
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeLine(index)}
                            disabled={editedLines.length <= 2}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Edit Totals Row */}
                    <TableRow className="bg-muted/50 font-semibold">
                      <TableCell colSpan={2} className="text-right">Total</TableCell>
                      <TableCell className={cn(
                        "text-right font-mono",
                        editTotals?.isBalanced ? "text-emerald-600" : "text-destructive"
                      )}>
                        {formatCurrency(editTotals?.totalDebit || 0)}
                      </TableCell>
                      <TableCell className={cn(
                        "text-right font-mono",
                        editTotals?.isBalanced ? "text-emerald-600" : "text-destructive"
                      )}>
                        {formatCurrency(editTotals?.totalCredit || 0)}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* View Mode */}
      {!isEditing && (
        <>
          {/* Entry Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Memo</p>
                <p className="font-medium">
                  {entry.memo || <span className="text-muted-foreground italic">No memo</span>}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium font-mono text-sm">
                  {new Date(entry.created_at).toLocaleString()}
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Posted</p>
                <p className="font-medium font-mono text-sm">
                  {entry.posted_at 
                    ? new Date(entry.posted_at).toLocaleString()
                    : <span className="text-muted-foreground">Not posted</span>
                  }
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Balance Check */}
          {!isBalanced && entry.status === 'draft' && (
            <Card className="border-destructive bg-destructive/10">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-destructive">
                  <XCircle className="w-5 h-5" />
                  <p className="font-medium">Entry Not Balanced</p>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Total Debit ({formatCurrency(entry.total_debit)}) does not equal Total Credit ({formatCurrency(entry.total_credit)}). 
                  The difference is {formatCurrency(Math.abs(entry.total_debit - entry.total_credit))}.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Lines Table */}
          <Card>
            <CardHeader>
              <CardTitle>Journal Lines</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-24">Account</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-32 text-right">Debit</TableHead>
                    <TableHead className="w-32 text-right">Credit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entry.lines.map((line) => (
                    <TableRow key={line.line_id}>
                      <TableCell>
                        <div>
                          <p className="font-mono font-medium">{line.account_code}</p>
                          <p className="text-xs text-muted-foreground">{line.account_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {line.description || <span className="text-muted-foreground italic">-</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {line.debit > 0 ? (
                          <span className="debit-amount">{formatCurrency(line.debit)}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {line.credit > 0 ? (
                          <span className="credit-amount">{formatCurrency(line.credit)}</span>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Totals Row */}
                  <TableRow className="bg-muted/50 font-semibold">
                    <TableCell colSpan={2} className="text-right">
                      Total
                    </TableCell>
                    <TableCell className="text-right font-mono debit-amount">
                      {formatCurrency(entry.total_debit)}
                    </TableCell>
                    <TableCell className="text-right font-mono credit-amount">
                      {formatCurrency(entry.total_credit)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Source Info */}
          {entry.source_type && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Source</p>
                <p className="font-medium">
                  {entry.source_type === 'reversal' 
                    ? 'Reversal Entry' 
                    : entry.source_type === 'replacement'
                    ? 'Replacement Entry'
                    : entry.source_type}
                  {entry.source_id && (
                    <Link 
                      to={`/journal-entries/${entry.source_id}`}
                      className="text-primary hover:underline ml-2"
                    >
                      View Original
                    </Link>
                  )}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          <Attachments 
            linkType="journal_entry"
            linkId={entry.entry_id}
            title="Attachments"
            canUpload={true}
            canDelete={entry.status === 'draft'}
          />
        </>
      )}

      {/* Void Confirmation Dialog */}
      <AlertDialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void Journal Entry #{entry.entry_no}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a reversing entry to offset all the amounts in this entry. 
              The original entry will be marked as voided and cannot be undone.
              <br /><br />
              A new journal entry will be created with debits and credits swapped to reverse 
              the financial effect.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoid}
              disabled={voiding}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {voiding ? 'Voiding...' : 'Void Entry'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Options Dialog for Posted Entries */}
      <Dialog open={editOptionsOpen} onOpenChange={setEditOptionsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Posted Entry</DialogTitle>
            <DialogDescription>
              Posted entries cannot be edited directly to maintain ledger integrity. 
              Choose how you'd like to make changes:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Card 
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => { setEditOptionsOpen(false); setReverseDialogOpen(true); }}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <RotateCcw className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Create Reversing Entry</p>
                    <p className="text-sm text-muted-foreground">
                      Creates a new entry that exactly reverses this one (swaps debits and credits). 
                      Both entries remain in the ledger for audit trail.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={handleReplace}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <ArrowRightLeft className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium">Create Replacement Entry</p>
                    <p className="text-sm text-muted-foreground">
                      Reverses this entry and creates a new draft copy for you to edit. 
                      Use this to correct errors or make changes to the original amounts.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOptionsOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reverse Confirmation Dialog */}
      <AlertDialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Reversing Entry?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will create a new journal entry that exactly reverses JE #{entry.entry_no}:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>All debits become credits and vice versa</li>
                <li>The original entry will be marked as "reversed"</li>
                <li>The reversal will be posted immediately</li>
                <li>Memo: "Reversal of JE#{entry.entry_no}"</li>
              </ul>
              <p className="text-amber-600 dark:text-amber-400 font-medium text-sm">
                This action cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReverse} disabled={posting}>
              {posting ? 'Creating...' : 'Create Reversal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
